'use client';

import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Common = {
  children: ReactNode;
  className?: string;
  icon?: LucideIcon;
} & ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * Нижний CTA: зелёный градиент, stadium (макет PVZ_app — «Начать смену» и т.п.).
 */
export function PwaPrimaryPillButton({ children, className, icon: Icon, ...props }: Common) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-bold text-white shadow-md transition',
        'bg-gradient-to-br from-[var(--primary-gradient-from)] to-[var(--primary-gradient-to)]',
        'hover:opacity-95 active:opacity-90 disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    >
      {Icon ? <Icon className="h-5 w-5 shrink-0" strokeWidth={2} /> : null}
      {children}
    </button>
  );
}

/**
 * Терминальное действие — завершение смены (оранжевый, макет PVZ_app / Dodo-like CTA).
 */
export function PwaTerminalPillButton({ children, className, icon: Icon, ...props }: Common) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-bold transition',
        'bg-gradient-to-b from-[var(--action-terminal-gradient-from)] to-[var(--action-terminal-gradient-to)]',
        'text-[var(--action-terminal-foreground)] shadow-md',
        'hover:opacity-95 active:opacity-90 disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    >
      {Icon ? <Icon className="h-5 w-5 shrink-0" strokeWidth={2} /> : null}
      {children}
    </button>
  );
}

/**
 * Вторичная stadium-капсула: «Не сейчас», «Закрыть», Cancel в stack-footer (Dodo-inspired).
 * Без бордера — мягкая заливка surface-strong.
 */
export function PwaSecondaryPillButton({ children, className, icon: Icon, ...props }: Common) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-bold transition',
        'bg-[var(--surface-strong)] text-[var(--foreground)]',
        'hover:brightness-95 active:opacity-90',
        'disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      {...props}
    >
      {Icon ? <Icon className="h-5 w-5 shrink-0" strokeWidth={2} /> : null}
      {children}
    </button>
  );
}
